import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { getSimpleBoolSelectItems, IGetValueArgs } from "@components/smart/FieldInfo";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy } from "@components/smart/smartTable";
import { ICellValueObject } from "@components/table";
import { getRouteByEntityType } from "@odata/EntityTypes";
import { IFormatOptions } from "@odata/OData.utils";
import { setReportDef } from "@pages/reports/Report.test.utils";
import i18next from "i18next";

import { BasicInputSizes, ConfigListItemBoundType, FieldType, Sort } from "../../../enums";
import { TRecordType, TValue } from "../../../global.types";
import BindingContext from "../../../odata/BindingContext";
import { CommonReportProps, getByDateDef } from "../CommonDefs";
import {
    getAccountsParamDef,
    IReportTableDefinition,
    NumberAggregationFunction,
    ReportConfigGroup,
    TimeAggregationFunction,
    TReportConfigListItemOverride
} from "../Report.utils";
import { ReportId } from "../ReportIds";
import { ISelectItem } from "@components/inputs/select/Select.types";

export const SaldoProps = {
    accounts: BindingContext.localContext("Accounts"),
    sumAmounts: BindingContext.localContext("SumAmounts"),
    showBalancedSaldo: BindingContext.localContext("ShowBalancedSaldo")
};

// warning, extended in Saldo.updateConfigListSettingsBasedOnSumAmounts
export const saldoConfigListSettings: TRecordType<TReportConfigListItemOverride> = {
    Account: {
        isRequired: true,
        isDisabled: true,
        isCopyOnly: false,
        group: ReportConfigGroup.Groups
    },
    JournalEntry_DocumentNumberOurs: {
        isRequired: true,
        isDisabled: true,
        isCopyOnly: false,
        group: ReportConfigGroup.Groups
    },
    JournalEntry_Amount: {
        isRequired: true,
        areItemsReadOnly: true,
        boundTo: ConfigListItemBoundType.Column
    },
    JournalEntry_Description: {
        isRequired: true,
        hideFromAvailable: true
    }
};


export const getDefinition = (): IReportTableDefinition => {
    const tableId = ReportId.Saldo;
    const title = i18next.t("Reporting:Saldo.Title");
    const path = "Saldo";
    const initialSortBy = [
        {
            id: `JournalEntry_DateAccountingTransaction_${TimeAggregationFunction.Day}`,
            sort: Sort.Asc
        }
    ];
    const disableAggregateButton = true;
    const parameters: string[] = [
        CommonReportProps.byDate,
        SaldoProps.accounts,
        SaldoProps.sumAmounts,
        SaldoProps.showBalancedSaldo
    ];

    const defaultReportHierarchy: IReportHierarchy = {
        "Aggregate": true,
        "Groups": [
            {
                "ColumnAlias": "Account"
            },
            {
                "ColumnAlias": "JournalEntry_DocumentNumberOurs"
            }
        ],
        "Columns": [
            {
                "ColumnAlias": "JournalEntry_DateAccountingTransaction",
                "AggregationFunction": TimeAggregationFunction.Day
            },
            {
                "ColumnAlias": "JournalEntry_JournalEntryNumberOurs"
            },
            {
                "ColumnAlias": "JournalEntry_SymbolVariable"
            },
            {
                "ColumnAlias": "JournalEntry_Description"
            }
        ],
        "Aggregations": [
            {
                "ColumnAlias": "JournalEntry_Amount",
                "AggregationFunction": NumberAggregationFunction.Sum
            }
        ]
    };

    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [
                CommonReportProps.byDate,
                SaldoProps.accounts,
                SaldoProps.sumAmounts,
                SaldoProps.showBalancedSaldo
            ],
            filterDefinition: {
                [CommonReportProps.byDate]: {
                    ...getByDateDef(),
                    label: i18next.t("Reporting:Saldo.ByDate")
                },
                [SaldoProps.accounts]: getAccountsParamDef(["321", "311"], false, true),
                [SaldoProps.sumAmounts]: {
                    type: FieldType.ComboBox,
                    defaultValue: true,
                    label: i18next.t("Reporting:Saldo.SumAmounts"),
                    fieldSettings: {
                        items: getSimpleBoolSelectItems()
                    }
                },
                [SaldoProps.showBalancedSaldo]: {
                    width: BasicInputSizes.L,
                    type: FieldType.ComboBox,
                    defaultValue: false,
                    label: i18next.t("Reporting:Saldo.ShowBalancedSaldo"),
                    fieldSettings: {
                        items: getSimpleBoolSelectItems()
                    }
                }
            }
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            defaultFilters: [],
            filterDefinition: {
                "##JournalEntry_Description##": {
                    fieldSettings: {
                        itemsForRender: (items: ISelectItem[], args: IGetValueArgs) => {
                            // virtual row for saldo report, which should't be used in filter, as it breaks whole report https://solitea-cz.atlassian.net/browse/DEV-36224
                            return items.filter((item => item.label !== i18next.t("Reporting:Saldo.ExchangeRateDifference").toString()));
                        }
                    }
                }
            }
        }
    ];

    const columnOverrides = {
        JournalEntry_JournalEntryNumberOurs: {
            formatter: (val: TValue, args: IFormatOptions): ICellValueObject => {
                if (!val) {
                    return null;
                }

                const docId = args.entity["JournalEntry_DocumentId"] ?? args.entity["JournalEntry_PaymentDocumentId"];
                const docType = args.entity["JournalEntry_DocumentTypeCode"];
                const url = `${getRouteByEntityType(docType)}/${docId}`;

                return getTableIntentLink(val as string, {
                    route: url,
                    context: args.storage.context,
                    storage: args.storage
                });
            }
        }
    };


    return {
        title, path, id: tableId, initialSortBy, disableAggregateButton, columnOverrides,
        filterBarDef, parameters, defaultReportHierarchy, configListItemsOverride: saldoConfigListSettings
    };
};

setReportDef(ReportId.Saldo, getDefinition);